import { useCallback, useEffect, useState } from 'react';

interface ServiceWorkerState {
  isUpdateAvailable: boolean;
  isUpdating: boolean;
  currentVersion: string | null;
  isInstalled: boolean;
  error: string | null;
}

interface ServiceWorkerActions {
  checkForUpdate: () => Promise<void>;
  forceUpdate: () => Promise<void>;
  skipWaiting: () => void;
}

/**
 * Custom hook for managing service worker updates and version checking
 */
export const useServiceWorkerUpdate = (): ServiceWorkerState & ServiceWorkerActions => {
  const [state, setState] = useState<ServiceWorkerState>({
    isUpdateAvailable: false,
    isUpdating: false,
    currentVersion: null,
    isInstalled: false,
    error: null,
  });

  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  // Check for service worker support
  const isSupported = 'serviceWorker' in navigator;

  // Get current service worker version
  const getCurrentVersion = useCallback(async (): Promise<string | null> => {
    if (!isSupported || !registration || !registration.active) return null;

    return new Promise(resolve => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = event => {
        resolve(event.data.version || null);
      };

      registration.active?.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);
    });
  }, [isSupported, registration]);

  // Check for updates
  const checkForUpdate = useCallback(async (): Promise<void> => {
    if (!isSupported || !registration) {
      console.log('Service Worker not supported or not registered');
      return;
    }

    try {
      setState(prev => ({ ...prev, error: null }));

      // Trigger update check
      await registration.update();
      // console.log('Service Worker: Update check completed');

      // Check if there's a waiting service worker
      if (registration.waiting) {
        setState(prev => ({ ...prev, isUpdateAvailable: true }));
      }
    } catch (error) {
      console.error('Service Worker: Update check failed', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Update check failed',
      }));
    }
  }, [isSupported, registration]);

  // Force update by clearing caches and activating new service worker
  const forceUpdate = useCallback(async (): Promise<void> => {
    if (!isSupported || !registration) {
      // console.log('Service Worker not supported or not registered');
      return;
    }

    try {
      setState(prev => ({ ...prev, isUpdating: true, error: null }));

      // Method 1: If there's a waiting service worker, activate it
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });

        // Wait for the new service worker to take control
        await new Promise<void>(resolve => {
          const handleControllerChange = () => {
            navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
            resolve();
          };
          navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
        });
      } else if (registration.active) {
        // Method 2: Force current service worker to clear caches
        await new Promise<void>(resolve => {
          const messageChannel = new MessageChannel();
          messageChannel.port1.onmessage = event => {
            if (event.data.type === 'CACHES_CLEARED') {
              // console.log('Service Worker: Caches cleared');
              resolve();
            }
          };

          registration.active?.postMessage({ type: 'FORCE_UPDATE' }, [messageChannel.port2]);
        });
      }

      // Don't reload here - let the component control when to reload
      setState(prev => ({ ...prev, isUpdating: false }));
    } catch (error) {
      // console.error('Service Worker: Force update failed', error);
      setState(prev => ({
        ...prev,
        isUpdating: false,
        error: error instanceof Error ? error.message : 'Force update failed',
      }));
    }
  }, [isSupported, registration]);

  // Skip waiting and activate new service worker
  const skipWaiting = useCallback(() => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }, [registration]);

  // Initialize service worker
  useEffect(() => {
    if (!isSupported) {
      // console.log('Service Worker not supported');
      return;
    }

    // Get current registration
    navigator.serviceWorker.getRegistration().then(reg => {
      if (reg) {
        setRegistration(reg);
        setState(prev => ({ ...prev, isInstalled: true }));

        // Get current version
        getCurrentVersion().then(version => {
          setState(prev => ({ ...prev, currentVersion: version }));
        });
      }
    });

    // Listen for service worker updates
    const handleUpdateFound = () => {
      // console.log('Service Worker: Update found');
      setState(prev => ({ ...prev, isUpdateAvailable: true }));
    };

    // Listen for controller changes (new service worker activated)
    const handleControllerChange = () => {
      // console.log('Service Worker: New service worker activated');
      setState(prev => ({
        ...prev,
        isUpdateAvailable: false,
        isUpdating: false,
      }));

      // Get new version
      getCurrentVersion().then(version => {
        setState(prev => ({ ...prev, currentVersion: version }));
      });
    };

    // Listen for service worker messages
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        // console.log('Service Worker: Updated to version', event.data.version);
        setState(prev => ({
          ...prev,
          currentVersion: event.data.version,
          isUpdateAvailable: false,
          isUpdating: false,
        }));
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Check for updates on registration
    navigator.serviceWorker.getRegistration().then(reg => {
      if (reg) {
        reg.addEventListener('updatefound', handleUpdateFound);

        // Check if there's already a waiting service worker
        if (reg.waiting) {
          setState(prev => ({ ...prev, isUpdateAvailable: true }));
        }
      }
    });

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [isSupported, getCurrentVersion]);

  // Auto-check for updates periodically
  useEffect(() => {
    if (!isSupported) return;

    const interval = setInterval(() => {
      checkForUpdate();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [isSupported, checkForUpdate]);

  return {
    ...state,
    checkForUpdate,
    forceUpdate,
    skipWaiting,
  };
};
